import { FC, useCallback, useEffect, useMemo } from 'react';
import { DropDownMultiFilterSelector } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { trackingIds } from '../../../../tracking-ids';
import { useCallIntelFiltersContext } from '../context/call-intel-filters.context';
import { FilterTraySection } from './filter-tray-section/filter-tray-section';

export interface OfficeNumberFilterTrayProps {
  disabled?: boolean;
  officeNumbers?: string[];
  showClear?: boolean;
}

export const OfficeNumberFilterTray: FC<OfficeNumberFilterTrayProps> = ({
  disabled,
  officeNumbers = [],
  showClear = true,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { filterValues, updateFilterValues, updateFilterModified, isLoading } = useCallIntelFiltersContext();

  const isDisabled = disabled || isLoading;

  const hasOptions = officeNumbers.length > 0;

  const officeNumberOptions = useMemo(
    () =>
      officeNumbers.map((number) => ({
        id: number,
        label: formatPhoneNumber(number),
      })),
    [officeNumbers]
  );

  const onChange = useCallback(
    (value: string[]) => {
      updateFilterValues({ officeNumbers: value });
    },
    [updateFilterValues]
  );

  useEffect(() => {
    updateFilterModified({ officeNumbers: !!filterValues.officeNumbers?.length });
  }, [filterValues.officeNumbers, updateFilterModified]);

  if (!hasOptions) {
    return null;
  }

  return (
    <FilterTraySection
      label={t('Office Number')}
      disableClear={isDisabled}
      showClear={showClear}
      onClear={() => onChange([])}
    >
      <DropDownMultiFilterSelector
        label={t('Select Office Number')}
        onChange={onChange}
        options={officeNumberOptions}
        trackingId={trackingIds.callIntel.officeNumbersFilter}
        value={filterValues.officeNumbers}
      />
    </FilterTraySection>
  );
};
